import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, type ReactNode } from 'react'

import { AuthContext, AUTH_SESSION_KEY, type AuthState } from './auth-context'
import { markDeliberateSignOut } from './sign-out-intent'
import { endSessionActivity } from './use-idle-timeout'
import { fetchSession, signOutLocally } from './session'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isPending, refetch } = useQuery({
    queryKey: AUTH_SESSION_KEY,
    queryFn: () => fetchSession(),

    retry: false,

    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })

  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const endSessionLocally = useCallback(() => {
    endSessionActivity()
    markDeliberateSignOut()

    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== AUTH_SESSION_KEY[0],
    })
    queryClient.setQueryData(AUTH_SESSION_KEY, null)
  }, [queryClient])

  const signOut = useCallback(async () => {
    endSessionLocally()
    await signOutLocally()
  }, [endSessionLocally])

  const value = useMemo<AuthState>(
    () => ({
      session: data ?? null,
      isLoading: isPending,
      refresh,
      signOut,
      endSessionLocally,
    }),
    [data, isPending, refresh, signOut, endSessionLocally],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
